import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import baseURL from "../../authComponents/baseURL/BaseURL";
import ProfileCard from "../../reusableComponents/ProfileCard";
import { FaBackward, FaForward } from "react-icons/fa";
import { toast } from "react-toastify";

const AllProfiles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const profilesPerPage = 12;
  const [gender, setGender] = useState(searchParams.get("gender") || "");
  const [marital_status, setMaritalStatus] = useState(
    searchParams.get("marital_status") || ""
  );
  const [religion, setReligion] = useState(searchParams.get("religion") || "");
  const [occupation, setOccupation] = useState(
    searchParams.get("occupation") || ""
  );
  const [age_min, setAge_min] = useState(searchParams.get("age_min") || "");
  const [age_max, setAge_max] = useState(searchParams.get("age_max") || "");

  const [world_country_id, setWorldCountryId] = useState(
    searchParams.get("world_country_id") || ""
  );
  const [world_division_id, setWorldDivisionId] = useState(
    searchParams.get("world_division_id") || ""
  );
  const [world_city_id, setWorldCityId] = useState(
    searchParams.get("world_city_id") || ""
  );

  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [cities, setCities] = useState([]);

  const [searchType, setSearchType] = useState(
    searchParams.get("searchType") || "local"
  );

  const resetFilters = () => {
    setGender("");
    setMaritalStatus("");
    setReligion("");
    setOccupation("");
    setAge_min("");
    setAge_max("");
    setWorldCountryId("");
    setWorldDivisionId("");
    setWorldCityId("");
    setDivisions([]);
    setCities([]);
  };

  useEffect(() => {
    const fetchInitialProfiles = async () => {
      try {
        const currentParams = Object.fromEntries(searchParams.entries());
        const filteredParams = Object.fromEntries(
          Object.entries(currentParams).filter(([_, v]) => v !== "")
        );
        const response = await baseURL.get("/all_profiles", {
          params: filteredParams,
        });
        setProfiles(response.data.data || []);
      } catch (error) {
        setError("Failed to load profiles.");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialProfiles();
  }, [location.search]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await baseURL.get("/get_all_countries");
        setCountries(response.data.countries);
      } catch (error) {
        console.error("Error fetching countries:", error);
        toast.error("Failed to load countries.");
      }
    };

    fetchCountries();
  }, []);

  const handleCountryChange = async (e) => {
    const selectedCountry = e.target.value;
    setWorldCountryId(selectedCountry);
    setWorldDivisionId("");
    setWorldCityId("");
    setDivisions([]);
    setCities([]);

    if (selectedCountry) {
      try {
        const response = await baseURL.get(
          `/get_world_divisions/${selectedCountry}`
        );
        setDivisions(response.data.divisions);
      } catch (error) {
        console.error("Error fetching divisions:", error);
        toast.error("Failed to load divisions.");
      }
    }
  };

  const handleDivisionChange = async (e) => {
    const selectedDivision = e.target.value;

    setWorldDivisionId(selectedDivision);
    setWorldCityId("");
    setCities([]);

    if (selectedDivision) {
      try {
        const response = await baseURL.get(
          `/get_all_cities/${selectedDivision}`
        );
        setCities(response.data.cities);
      } catch (error) {
        console.error("Error fetching cities:", error);
        toast.error("Failed to load cities.");
      }
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const searchParams = new URLSearchParams();

      if (searchType) searchParams.set("searchType", searchType);

      const filters = {
        gender,
        marital_status,
        religion,
        occupation,
        age_min,
        age_max,
        world_country_id,
        world_division_id,
        world_city_id,
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) searchParams.set(key, value);
      });
      navigate(`/all-profiles?${searchParams.toString()}`, { replace: true });
      const response = await baseURL.get("/all_profiles", {
        params: Object.fromEntries(searchParams),
      });
      setProfiles(response.data.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      setError(
        error.response?.data?.message ||
          "Failed to load profiles. Please try again later."
      );
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-[#730000]"></div>
        <p className="ml-4 text-[#730000] font-bold">Loading Profiles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 font-extrabold text-2xl min-h-screen mt-20">
        <p>{error}</p>
        <button
          onClick={() => {
            setError(null);
          }}
          className="mt-4 px-4 py-2 bg-[#730000] text-white rounded"
        >
          Try Again
        </button>
      </div>
    );
  }

  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = profiles.slice(
    indexOfFirstProfile,
    indexOfLastProfile
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(profiles.length / profilesPerPage);
  return (
    <div className="p-10 max-md:p-4 min-h-screen">
      <div className="mb-4">
        <p className="text-lg font-bold text-[#730000] mb-2">
          Search Preferences
        </p>

        <div className="flex gap-4 mb-4">
          <button
            onClick={() => {
              setSearchType("local");
              resetFilters();
            }}
            className={`px-4 py-2 border rounded ${
              searchType === "local" ? "bg-[#730000] text-white" : ""
            }`}
          >
            Local Search
          </button>
          <button
            onClick={() => {
              setSearchType("international");
              resetFilters();
            }}
            className={`px-4 py-2 border rounded ${
              searchType === "international" ? "bg-[#730000] text-white" : ""
            }`}
          >
            International Search
          </button>
        </div>

        {/* Conditional Filters */}
        <div className="flex flex-wrap gap-2 justify-between">
          {/* "Local" Search Filters */}
          {searchType === "local" && (
            <>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="px-4 py-2 border rounded w-42"
              >
                <option value="">I am looking for</option>
                <option value="female">Woman</option>
                <option value="male">Man</option>
              </select>
              <select
                value={marital_status}
                onChange={(e) => setMaritalStatus(e.target.value)}
                className="px-4 py-2 border rounded w-40"
              >
                <option value="">Marital Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="separated">Separated</option>
                <option value="widow">Widow (Husband Died)</option>
                <option value="widower">Widower (Wife Died)</option>
              </select>
              <select
                value={religion}
                onChange={(e) => setReligion(e.target.value)}
                className="px-4 py-2 border rounded w-36"
              >
                <option value="">Religion</option>
                <option value="islam">Islam</option>
                <option value="christianity">Christianity</option>
                <option value="hinduism">Hinduism</option>
                <option value="buddhism">Buddhism</option>
                <option value="other_religion">Other</option>
              </select>
              <select
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                className="px-4 py-2 border rounded w-36"
              >
                <option value="">Profession</option>
                <option value="doctor">Doctor</option>
                <option value="engineer">Engineer</option>
                <option value="css_officer">CSS Officer</option>
                <option value="cyber_security">Cyber Security</option>
                <option value="teacher">Teacher</option>
                <option value="business_man">Business Man</option>
                <option value="armed_forces">Armed Forces</option>
                <option value="other">Other</option>
              </select>

              <select
                value={age_min}
                onChange={(e) => setAge_min(e.target.value)}
                className="px-4 py-2 border rounded w-34"
              >
                <option value="">Min Age</option>
                {[...Array(33)].map((_, index) => (
                  <option key={index} value={18 + index}>
                    {18 + index}
                  </option>
                ))}
              </select>
              <select
                value={age_max}
                onChange={(e) => setAge_max(e.target.value)}
                className="px-4 py-2 border rounded w-34"
              >
                <option value="">Max Age</option>
                {[...Array(33)].map((_, index) => (
                  <option key={index} value={18 + index}>
                    {18 + index}
                  </option>
                ))}
              </select>

              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-[#730000] text-white rounded hover:bg-[#5a0000] mt-2"
              >
                Search
              </button>
            </>
          )}

          {/* "International" Search Filters */}
          {searchType === "international" && (
            <>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="px-4 py-2 border rounded w-42"
              >
                <option value="">I am looking for</option>
                <option value="female">Woman</option>
                <option value="male">Man</option>
              </select>
              <select
                value={marital_status}
                onChange={(e) => setMaritalStatus(e.target.value)}
                className="px-4 py-2 border rounded w-40"
              >
                <option value="">Marital Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="separated">Separated</option>
                <option value="widow">Widow (Husband Died)</option>
                <option value="widower">Widower (Wife Died)</option>
              </select>
              <select
                value={religion}
                onChange={(e) => setReligion(e.target.value)}
                className="px-4 py-2 border rounded w-36"
              >
                <option value="">Religion</option>
                <option value="islam">Islam</option>
                <option value="christianity">Christianity</option>
                <option value="hinduism">Hinduism</option>
                <option value="buddhism">Buddhism</option>
                <option value="other_religion">Other</option>
              </select>
              <select
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                className="px-4 py-2 border rounded w-36"
              >
                <option value="">Profession</option>
                <option value="doctor">Doctor</option>
                <option value="engineer">Engineer</option>
                <option value="css_officer">CSS Officer</option>
                <option value="cyber_security">Cyber Security</option>
                <option value="teacher">Teacher</option>
                <option value="business_man">Business Man</option>
                <option value="armed_forces">Armed Forces</option>
                <option value="other">Other</option>
              </select>
              {/* First id */}
              <select
                value={world_country_id}
                onChange={handleCountryChange}
                className="px-4 py-2 border rounded w-36"
              >
                <option value="">Country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
              {/* Division Dropdown */}
              <select
                value={world_division_id}
                onChange={handleDivisionChange}
                className="px-4 py-2 border rounded w-36"
                disabled={!world_country_id}
              >
                <option value="">Division</option>
                {divisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
              {/* City Dropdown */}
              <select
                value={world_city_id}
                onChange={(e) => setWorldCityId(e.target.value)}
                className="px-4 py-2 border rounded w-36"
                disabled={!world_division_id}
              >
                <option value="">City</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
              <select
                value={age_min}
                onChange={(e) => setAge_min(e.target.value)}
                className="px-4 py-2 border rounded w-34"
              >
                <option value="">Min Age</option>
                {[...Array(33)].map((_, index) => (
                  <option key={index} value={18 + index}>
                    {18 + index}
                  </option>
                ))}
              </select>
              <select
                value={age_max}
                onChange={(e) => setAge_max(e.target.value)}
                className="px-4 py-2 border rounded w-34"
              >
                <option value="">Max Age</option>
                {[...Array(33)].map((_, index) => (
                  <option key={index} value={18 + index}>
                    {18 + index}
                  </option>
                ))}
              </select>

              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-[#730000] text-white rounded hover:bg-[#5a0000] mt-2"
              >
                Search
              </button>
            </>
          )}

          {/* Search Button */}
        </div>
      </div>

      {/* Profiles Display */}
      <p className="text-2xl font-bold text-[#730000] mb-2">Profiles</p>
      <div className="grid grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1 max-md:p-2 justify-start max-sm:text-[10px] gap-6 font-serif">
        {currentProfiles.map((profile) => (
          <ProfileCard key={profile.id} profile={profile} />
        ))}
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-end mt-10 overflow-x-scrol font-serif">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 border rounded-l-lg text-[#730000] hover:bg-[#730000] hover:text-white disabled:opaworld_city_id-50"
        >
          <FaBackward />
        </button>
        <div className="">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index + 1}
              onClick={() => paginate(index + 1)}
              className={`px-3 py-2 border-t border-b min-w-10 text-sm font-semibold font-serif ${
                currentPage === index + 1
                  ? "bg-[#730000] text-white"
                  : "bg-white text-[#730000]"
              } hover:bg-[#730000] hover:text-white transition duration-200`}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 border rounded-r-lg text-[#730000] hover:bg-[#730000] hover:text-white disabled:opaworld_city_id-50"
        >
          <FaForward />
        </button>
      </div>
    </div>
  );
};

export default AllProfiles;
